"use client";

import { useState } from "react";
import { Wallet, CheckCircle, AlertTriangle } from "lucide-react";
import { useUserStore } from "@/store/trade/user";
import { useNotifications } from "@/components/ui/notification-provider";
import { useTheme } from "@/context/ThemeContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PasskeyCreatedInfo } from "@/components/passkey-created-info";

export const CompactWalletIndicator = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { addNotification } = useNotifications();
  const userStore = useUserStore();
  const user = userStore.user;
  const setUser = userStore.setUser;
  const isConnected = !!user;
  const [isConnecting, setIsConnecting] = useState(false);
  const [showNewWalletDialog, setShowNewWalletDialog] = useState(false);
  const [createdPasskeyInfo, setCreatedPasskeyInfo] = useState<{
    publicKeyX: string;
    publicKeyY: string;
    credentialId: string;
    walletAddress: string;
  } | null>(null);

  const handleConnect = async () => {
    if (isConnecting) return;

    setIsConnecting(true);

    try {
      console.log("🔐 Attempting passkey discovery...");

      // Try to discover any existing passkey on this device
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      try {
        // Discoverable credential flow - browser shows all passkeys for this RP
        const credential = (await navigator.credentials.get({
          publicKey: {
            challenge,
            rpId:
              window.location.hostname === "otc.network"
                ? "otc.network"
                : "localhost",
            userVerification: "required",
            timeout: 60000,
          },
          mediation: "optional",
        })) as PublicKeyCredential | null;

        if (credential) {
          console.log("✅ Found existing passkey:", credential.id);

          // Authenticate with backend
          const authResponse = await fetch(
            "/api/auth/passkey/authenticate-passkey",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ credentialId: credential.id }),
            },
          );

          const authResult = await authResponse.json();

          if (authResponse.ok && authResult.user) {
            const userData = {
              id: authResult.user.id,
              passkey: authResult.user.passkey,
              credentialId: credential.id,
              chainId: 175,
              status: authResult.user.status || "ACTIVE",
            };
            setUser(userData);
            // Wallet address for display only (not a secret) — session cookie is set HttpOnly by backend
            localStorage.setItem("otc_passkey_address", userData.passkey);

            addNotification({
              type: 'success',
              title: "Wallet Connected",
              message: "Connected with passkey",
            });
            return;
          }
        }
      } catch (discoveryError: any) {
        console.log("⚠️ Passkey discovery failed:", discoveryError.message);
      }

      // No passkey found - show confirmation dialog
      setIsConnecting(false);
      setShowNewWalletDialog(true);
    } catch (error: any) {
        addNotification({
          type: 'error',
          title: "Connection Failed",
          message: error.message || "Failed to connect",
        });
    } finally {
      setIsConnecting(false);
    }
  };

  const createNewWallet = async () => {
    setShowNewWalletDialog(false);
    setIsConnecting(true);

    try {
      console.log("🔐 Creating new Porto smart wallet...");

      // Use consistent passkey name with timestamp
      const passkeyName = `Passkey-${Date.now().toString().slice(-8)}`;

      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const credential = (await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            name: "OTC Network",
            id:
              window.location.hostname === "otc.network"
                ? "otc.network"
                : "localhost",
          },
          user: {
            id: new Uint8Array(32),
            name: passkeyName,
            displayName: passkeyName,
          },
          pubKeyCredParams: [{ alg: -7, type: "public-key" as const }],
          authenticatorSelection: {
            authenticatorAttachment: "platform" as const,
            userVerification: "required" as const,
          },
          timeout: 60000,
          attestation: "direct" as const,
        },
      })) as PublicKeyCredential;

      if (!credential) throw new Error("Passkey creation cancelled");

      const response = credential.response as AuthenticatorAttestationResponse;
      const publicKeyBuffer = response.getPublicKey();
      if (!publicKeyBuffer) throw new Error("Failed to extract public key");

      const publicKeyArray = new Uint8Array(publicKeyBuffer);
      let xBytes: Uint8Array | null = null;
      let yBytes: Uint8Array | null = null;

      for (let i = publicKeyArray.length - 65; i >= 0; i--) {
        if (publicKeyArray[i] === 0x04 && i + 65 <= publicKeyArray.length) {
          xBytes = publicKeyArray.slice(i + 1, i + 33);
          yBytes = publicKeyArray.slice(i + 33, i + 65);
          break;
        }
      }

      if (!xBytes || !yBytes) throw new Error("Failed to parse public key");

      const xHex =
        "0x" +
        Array.from(xBytes)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      const yHex =
        "0x" +
        Array.from(yBytes)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

      const registerResponse = await fetch("/api/auth/passkey/smart/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          credentialId: credential.id,
          publicKey: { x: xHex, y: yHex },
        }),
      });

      const registerResult = await registerResponse.json();
      if (!registerResponse.ok || !registerResult.user)
        throw new Error(registerResult.message || "Failed to register");

      const smartWalletAddress = registerResult.user.smartWalletAddress;

      // Show the public key info instead of just toast
      setCreatedPasskeyInfo({
        publicKeyX: xHex,
        publicKeyY: yHex,
        credentialId: credential.id,
        walletAddress: smartWalletAddress,
      });

      const userData = {
        id: registerResult.user.id,
        passkey: smartWalletAddress,
        credentialId: credential.id,
        chainId: 175,
        status: "ACTIVE",
      };
      setUser(userData);
      // Wallet address for display only (not a secret) — session cookie is set HttpOnly by backend
      localStorage.setItem("otc_passkey_address", userData.passkey);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: "Creation Failed",
        message: error.message || "Failed to create wallet",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  if (isConnected) {
    // Show small connected indicator
    return (
      <div
        className={`flex items-center gap-1 px-1.5 py-1 rounded text-xs ${
          isDark
            ? "bg-green-500/10 text-green-400 border border-green-500/20"
            : "bg-green-50 text-green-600 border border-green-200"
        }`}
      >
        <CheckCircle size={10} />
        <span className="hidden md:inline">Connected</span>
        <span className="md:hidden text-[10px]">●</span>
      </div>
    );
  }

  // Show connect button
  return (
    <>
      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className={`flex items-center gap-1 px-1.5 py-1 rounded text-xs font-medium transition-colors ${
          isDark
            ? "bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30"
            : "bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
        } ${isConnecting ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {isConnecting ? (
          <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <Wallet size={10} />
        )}
        <span className="hidden md:inline">
          {isConnecting ? "Connecting..." : "Connect"}
        </span>
        <span className="md:hidden text-[10px]">
          {isConnecting ? "..." : "Connect"}
        </span>
      </button>

      {/* New Wallet Confirmation Dialog */}
      <Dialog open={showNewWalletDialog} onOpenChange={setShowNewWalletDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              No Wallet Found
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-2">
              <div>
                <strong>No existing passkey was found on this device.</strong>
              </div>
              <div className="text-sm text-muted-foreground">
                If you already have a wallet on another device, please use that
                device.
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                ⚠️ Creating a new wallet will generate a new address.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowNewWalletDialog(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={createNewWallet}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-blue-500 text-white"
            >
              Create New Wallet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passkey Created Info Dialog */}
      <Dialog open={!!createdPasskeyInfo} onOpenChange={() => setCreatedPasskeyInfo(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {createdPasskeyInfo && (
            <PasskeyCreatedInfo
              publicKeyX={createdPasskeyInfo.publicKeyX}
              publicKeyY={createdPasskeyInfo.publicKeyY}
              credentialId={createdPasskeyInfo.credentialId}
              walletAddress={createdPasskeyInfo.walletAddress}
              onClose={() => setCreatedPasskeyInfo(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompactWalletIndicator;
